

// src/components/HostsTable/HostsTable.jsx
import React, { useState, useEffect, useRef } from "react"
import axios from "axios";
import styled from "styled-components"
import { useResto } from "../../../../hooks/useResto"
import { useUser } from "../../../../hooks/useUser"
import { useEvaluation } from "../../../../hooks/useEvaluations"
import { getCeoResto } from "../../../../services/restoHostService"
import { useRestoUsers } from "../../../../hooks/useRestoUsers"
import UnifiedEvaluationsTable from '../../desktop/components/UnifiedEvaluationsTable'
import { useOwnerRestaurantSelection } from "../../../../hooks/useOwnerRestaurantSelection"
import endpoint from "../../../../Endpoint";
import AxiosService from "../../../../lib/axios";

const Select = styled.select`
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  width: 100%;
  margin-bottom: 8px;
  opacity: ${({ $loading }) => ($loading ? 0.6 : 1)};
`
const Evaluations = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 20px auto;
  font-family: "Poppins", sans-serif;

  @media (max-width: 768px) {
    margin: 16px auto;
  }
`;

const EvaluationsHeader = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 2px solid #000;
  margin-bottom: 15px;
  padding-bottom: 12px;

  h2 {
    margin: 0 0 10px 0;
    font-size: 20px;
    font-weight: 600;
  }
`;

const HeaderControls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const DownloadButton = styled.button`
  align-self: flex-start;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export function EvaluationTab({ restoName = "Mi restaurante", forceRestaurantOnly = false }) {
  const { resto, getResto } = useResto();
  const { isCEO, isOwner } = useUser();
  const { getEvalByResto, getEvalRestoById, loading } = useEvaluation()
  const { getAllRestoHost, hostResto, loading: hostsLoading } = useRestoUsers();
  const { selectedOwnerRestaurantId, selectedOwnerRestaurant } = useOwnerRestaurantSelection();

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)
  const [name, setName] = useState(restoName)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // States for unified logic
  const [selectedType, setSelectedType] = useState("restaurant"); // "restaurant" o "host"
  const [selectedHostId, setSelectedHostId] = useState(null);

  // Ref para evitar cargas múltiples de hosts
  const hasLoadedHosts = useRef(false);

  useEffect(() => {
    if (!isOwner || !forceRestaurantOnly) return;
    const selected = selectedOwnerRestaurant;
    const ownerRestoId = String(
      selectedOwnerRestaurantId || selected?._id || selected?.id || selected?.place_id || ""
    );
    const ownerRestoName = selected?.name || restoName;
    setSelectedRestaurant(ownerRestoId || null);
    setName(ownerRestoName);
    setSelectedType("restaurant");
    setSelectedHostId(null);
  }, [forceRestaurantOnly, isOwner, restoName, selectedOwnerRestaurant, selectedOwnerRestaurantId]);

  useEffect(() => {
    if (!isOwner || !forceRestaurantOnly) return;
    const ownerRestoId = String(
      selectedOwnerRestaurantId || selectedOwnerRestaurant?._id || selectedOwnerRestaurant?.id || selectedOwnerRestaurant?.place_id || ""
    );
    if (!ownerRestoId) return;
    getEvalRestoById(ownerRestoId);
  }, [forceRestaurantOnly, getEvalRestoById, isOwner, selectedOwnerRestaurant, selectedOwnerRestaurantId]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isInitialLoad) return;

      try {
        setIsInitialLoad(false);
        let resolvedResto = resto;

        if (!isOwner && !isCEO && !resolvedResto?._id && !resolvedResto?.id && !resolvedResto?.place_id) {
          const restoResponse = await getResto();
          resolvedResto = restoResponse?.data || restoResponse || null;
        }

        // Carga inicial de datos de evaluaciones (restaurante actual)
        const currentRestoId =
          resolvedResto?._id || resolvedResto?.id || resolvedResto?.place_id || null;
        await getEvalByResto(currentRestoId);

        // Cargar lista de restaurantes
        if (isOwner && forceRestaurantOnly) {
          if (selectedOwnerRestaurant) {
            if (isMounted) {
              setRestaurants([selectedOwnerRestaurant]);
              setSelectedRestaurant(String(selectedOwnerRestaurantId || selectedOwnerRestaurant?._id || selectedOwnerRestaurant?.id || ""));
              setName(selectedOwnerRestaurant?.name || restoName);
            }
          }
        } else if (isCEO) {
          const restos = await getCeoResto();
          const restosArray = Array.isArray(restos) ? restos : (restos?.data ? (Array.isArray(restos.data) ? restos.data : [restos.data]) : []);

          if (isMounted) {
            setRestaurants(restosArray);
          }
        } else {
          if (resolvedResto && (resolvedResto._id || resolvedResto.id || resolvedResto.place_id)) {
            if (isMounted) {
              setRestaurants([resolvedResto]);
            }
          } else {
            try {
              await getResto();
            } catch (err) {
              console.error('❌ [EvaluationTab] Error cargando resto:', err);
            }
          }
        }
      } catch (err) {
        console.error("Error cargando datos iniciales:", err);
        if (isMounted) setIsInitialLoad(true);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [forceRestaurantOnly, getEvalByResto, getResto, isCEO, isOwner, resto, restoName, selectedOwnerRestaurant, selectedOwnerRestaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar hosts cuando se selecciona un restaurante
  useEffect(() => {
    if (isOwner && forceRestaurantOnly) return;
    // Resetear hosts al cambiar de restaurante
    hasLoadedHosts.current = false;

    const fetchHosts = async () => {
      if (!selectedRestaurant || selectedRestaurant === 'all') return;

      try {
        await getAllRestoHost(selectedRestaurant);
      } catch (err) {
        console.error("Error loading hosts:", err);
      }
    }

    fetchHosts();

    // Reset selection to restaurant when changing basic restaurant context
    setSelectedType("restaurant");
    setSelectedHostId(null);

  }, [forceRestaurantOnly, isOwner, selectedRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps -- keep getAllRestoHost out to prevent selection reset on re-render


  // Actualizar restaurantes cuando resto cambie (para usuarios no-CEO)
  useEffect(() => {
    if (!isCEO && resto && resto._id) {
      setRestaurants([resto]);
      if (!selectedRestaurant) {
        setSelectedRestaurant(resto._id);
      }
    }
  }, [resto, isCEO, selectedRestaurant]);

  const handleRestoChange = async (value) => {
    setSelectedRestaurant(value);

    if (value === "all") {
      await getEvalByResto();
      setName(restoName);
    } else {
      await getEvalRestoById(value);
      const chosenResto = restaurants.find(e => String(e._id || e.id) === value);
      setName(chosenResto?.name || restoName);
    }

    // Al cambiar restaurante, volver a vista general
    setSelectedType("restaurant");
    setSelectedHostId(null);
  }

  const handleViewChange = (e) => {
    const val = e.target.value;
    if (val === "restaurant") {
      setSelectedType("restaurant");
      setSelectedHostId(null);
    } else {
      setSelectedType("host");
      setSelectedHostId(val);
    }
  }

  // Filtrar hosts para el restaurante seleccionado si es necesario
  // En useRestoUsers normal, trae los del restaurante activo.
  // Si soy CEO, getAllRestoHost puede traer diferentes cosas dependiendo de la implementación.
  // Asumiremos que el filtrado se hace en backend o que recibimos todos y filtramos aca si tienen restoId.
  const currentRestoHosts = (hostResto || []).filter(h => {
    if (!selectedRestaurant || selectedRestaurant === 'all') return true;
    // Si el host tiene restoId, filtramos. Si no, mostramos todos (fallback)
    return h.restoId ? String(h.restoId) === String(selectedRestaurant) : true;
  });

  const resolvedRestoId = String(
    selectedRestaurant ||
    selectedOwnerRestaurantId ||
    selectedOwnerRestaurant?._id ||
    selectedOwnerRestaurant?.id ||
    resto?._id ||
    resto?.id ||
    resto?.place_id ||
    ""
  );
  const canDownloadHostCsv = Boolean(
    selectedType === "host" &&
    selectedHostId &&
    resolvedRestoId
  );

  const handleDownloadHostCsv = async () => {
    if (!canDownloadHostCsv) return;

    try {
      const token = AxiosService.getToken();
      const response = await axios({
        method: "GET",
        url: `${endpoint}/api/v2/evaluations/csv/host/${selectedHostId}/${resolvedRestoId}`,
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evaluaciones-host-${selectedHostId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error descargando CSV del host:", err);
    }
  };

  return (
    <>
      <Evaluations>
        <EvaluationsHeader>
          <h2>Evaluaciones</h2>
          <HeaderControls>
            {/* Selector de Restaurante (Visible solo si hay más de 1) */}
            {!forceRestaurantOnly && restaurants.length > 1 && (
              <Select
                value={selectedRestaurant || "all"}
                onChange={e => handleRestoChange(e.target.value)}
                disabled={loading}
                $loading={loading}
              >
                <option value="all">Todos los restaurantes</option>
                {restaurants.map(r => {
                  const rId = r._id || r.id;
                  const rName = r.name || 'Sin nombre';
                  return (
                    <option key={rId} value={rId}>
                      {rName} {r.country ? `(${r.country})` : ''}
                    </option>
                  )
                })}
              </Select>
            )}

            {/* Selector de Vista (Restaurante vs Camareros) */}
            {!forceRestaurantOnly && (
              <Select
                value={selectedType === "restaurant" ? "restaurant" : selectedHostId}
                onChange={handleViewChange}
                disabled={hostsLoading}
                $loading={hostsLoading}
              >
                <option value="restaurant">{hostsLoading ? "Cargando..." : (name || "Restaurante")}</option>
                {currentRestoHosts.map(host => (
                  <option key={host._id || host.id} value={host._id || host.id}>
                    {host.name || host.firstName}
                  </option>
                ))}
              </Select>
            )}
            {(selectedType === "host" && selectedHostId) && (
              <DownloadButton
                type="button"
                onClick={handleDownloadHostCsv}
                disabled={!canDownloadHostCsv}
              >
                Descargar CSV del host
              </DownloadButton>
            )}
          </HeaderControls>
        </EvaluationsHeader>

        {/* Tabla Unificada */}
        <UnifiedEvaluationsTable
          selectedType={forceRestaurantOnly ? "restaurant" : selectedType}
          selectedHostId={forceRestaurantOnly ? null : selectedHostId}
          restoName={name}
          restoId={selectedRestaurant}
          disableRestoFetch={true}
        />
      </Evaluations>

      <BottomSpacer />
    </>
  )
}

const BottomSpacer = styled.div`
  height: 100px;
`;
